/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import ScalingWidgetStyles from './ScalingWidget.styles';

export interface ChartOptions {
    path?: string;
    value?: string;
}

export interface Image {
    imageSrc: string;
    imageAlt: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    ratings: string;
    typographyClass?: string;
    titles: Headings[];
    disableTabStaticData?: boolean;
}

const getScalingWidgetTiles = (
    selectedTile: number,
    ratings: string[],
    tabIndex: number
): JSX.Element[] => {
    const content: JSX.Element[] = [];

    for (let i = 0; i < ratings.length; i++) {
        const selectedTileClassName: string = selectedTile === i + 1 ? 'selected' : '';
        content.push(
            <div className="tile" key={i}>
                <div
                    className={`shape shape-${i + 1} ${selectedTileClassName}`}
                    data-testid={`${selectedTileClassName}Tile`}
                    tabIndex={tabIndex}
                >
                    <div className="color-swatch"></div>
                </div>
                <div
                    className={`scale-cell cell-${i + 1} ${selectedTileClassName}`}
                    data-testid={`cell-${i + 1}`}
                    tabIndex={tabIndex}
                ></div>
                <div className={`label label-${i + 1}`} tabIndex={tabIndex}>
                    {ratings[i]}
                </div>
            </div>
        );
    }

    return content;
};

const ScalingWidget = (props: commonTypes.WidgetInstanceProps): JSX.Element => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const { styles, ratings, dataKey, titles, disableTabStaticData } = widgetProperties || props;
    const { data } = props;
    const tabIndex = globalDisableTabStaticData || disableTabStaticData ? -1 : 0;

    let ratingValue: any, _ratings: string;
    if (dataKey) {
        ratingValue = JsonQuery(dataKey as string, { data })?.value;
        _ratings = JsonQuery(ratings as string, { data }).value || ratings;
    } else {
        ratingValue = '002';
        _ratings = 'ccc,bbb,aaa';
    }

    return (
        <div
            className={`scaling-widget ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${ScalingWidgetStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles}
            `}
        >
            <HeaderWrapper
                headings={titles}
                data={data}
                disableTabStaticData={globalDisableTabStaticData || disableTabStaticData}
            />
            <div className="row" data-testid={'row'} role="img">
                {getScalingWidgetTiles(
                    parseInt(ratingValue),
                    _ratings?.toString().split(',') || [],
                    tabIndex
                )}
            </div>
        </div>
    );
};

export default ScalingWidget;
